import {
  AlertFilled,
  CaretDownOutlined,
  CaretUpOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  FileTextFilled,
  FireFilled,
  FrownFilled,
  HeartFilled,
  MedicineBoxFilled,
  MehFilled,
  PlusOutlined,
  SmileFilled,
  ThunderboltFilled,
  WarningFilled,
} from '@ant-design/icons';
import { clearCache, useRequest } from 'ahooks';
import ColorHash from 'color-hash-ts';
import { SEXUAL_ACTIVITY_COLORS } from 'components/charts/SexualActivityChart';
import { VAGINAL_DISCHARGE_COLORS } from 'components/charts/VaginalDischargesColors';
import SymptomTrackingReminder from 'components/SymptomTrackingReminder';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Spinner } from 'components/ui/spinner';
import { Textarea } from 'components/ui/textarea';
import { Dayjs } from 'dayjs';
import { usePreferenceQuery } from 'hooks/usePreferenceQuery';
import usePreferences from 'hooks/usePreferences';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import { cn } from 'lib/utils';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { usePushNotificationOptIn } from 'push/PushNotificationOptInContext';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';
import type { DailyStatus } from 'services/api/models/SymptomTrackingDTO';
import Platform from 'utils/Platform';
import Drop from '../assets/drop.svg?react';
import Signal0 from '../assets/signal-0.svg?react';
import Signal1 from '../assets/signal-1.svg?react';
import Signal2 from '../assets/signal-2.svg?react';
import Signal3 from '../assets/signal-3.svg?react';

const colorHash = new ColorHash();

/** The five-step severity scale as text colour classes (`chart-severity-*`). */
const SEVERITY_TEXT: Record<string, string> = {
  veryGood: 'text-chart-severity-very-good',
  good: 'text-chart-severity-good',
  average: 'text-chart-severity-average',
  bad: 'text-chart-severity-bad',
  veryBad: 'text-chart-severity-very-bad',
};

const INLINE_SVG = 'inline-block size-[1em] align-[-0.125em]';

const QUESTION_ICONS = {
  period: <Drop fill="currentColor" className={cn(INLINE_SVG, 'text-chart-severity-very-bad')} />,
  generalState: <SmileFilled className="text-chart-severity-very-good" />,
  pain: <AlertFilled className="text-chart-severity-bad" />,
  symptoms: <MedicineBoxFilled className="text-chart-severity-average" />,
  vaginalDischarges: <Drop fill="currentColor" className={cn(INLINE_SVG, 'text-chart-4')} />,
  sexualActivity: <HeartFilled className="text-warning" />,
  bodyTemperature: <FireFilled className="text-destructive-text" />,
  notes: <FileTextFilled className="text-subtle-foreground" />,
};

/* --- Layout helpers ------------------------------------------------------ */

const SCROLLBAR_HIDDEN = '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/** Section heading: the question icon followed by its label. */
function SectionTitle({ children }: { children: ReactNode }) {
  return <h5 className="mt-6 mb-1">{children}</h5>;
}

/** A horizontally scrolling answer row, faded out on its right edge. */
function AnswerRow({ twoRows, children }: { twoRows?: boolean; children: ReactNode }) {
  return (
    <div className="relative after:pointer-events-none after:absolute after:top-0 after:right-0 after:z-[2] after:h-full after:w-5 after:bg-gradient-to-l after:from-card after:to-transparent">
      <div
        className={cn(
          'overflow-x-auto pt-0.5 pr-4 pb-2.5',
          SCROLLBAR_HIDDEN,
          twoRows
            ? 'grid grid-flow-col grid-rows-2 auto-cols-[minmax(80px,max-content)] gap-x-3 gap-y-4'
            : 'flex gap-3',
        )}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * A toggle answer: a square tile, icon above a small centred label; `selected`
 * draws the ring. The tile is its own control, not a sized text button, hence
 * the full restyle.
 */
function AnswerButton({ className, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <Button
      variant="secondary"
      {...props}
      className={cn(
        'h-20 w-20 min-w-20 shrink-0 flex-col gap-0 rounded-lg bg-background p-2 text-center text-xs leading-tight break-words whitespace-normal hyphens-auto data-[selected=true]:bg-accent [&_.anticon]:mb-1 [&_.anticon]:text-xl',
        className,
      )}
    />
  );
}

interface SymptomTrackingSurveyModalProps {
  open: boolean;
  onClose: () => void;
  date: Dayjs;
}

export default function SymptomTrackingSurveyModal({ open, onClose, date }: SymptomTrackingSurveyModalProps) {
  // Hooks
  const { t } = useTranslation();

  const { getSpecificDailyStatus, refresh, hasTrackingBeenUsed, getFertilityStatusForDay } = useSymptomTrackingData();
  const wasPeriodQuickfilled = getSpecificDailyStatus(date)?.menstruation_amount;

  const isEditing = getSpecificDailyStatus(date) !== undefined;

  const [extraSymptomsValue, setExtraSymptomsValue] = useState<string>('');
  const [freeNotes, setFreeNotes] = useState<string>('');

  const [survey, setSurvey] = useState({
    date: date.format('YYYY-MM-DD'),
    general_state: null as number | null,
    menstruation_amount: wasPeriodQuickfilled || 0,
    pain_level: null as number | null,
    symptoms: new Set<string>([]),
    vaginal_discharges: new Set<string>([]),
    sexual_activity: null as DailyStatus['sexual_activity'],
    body_temperature: null as number | null,
  });

  const [showExtraSymptomsField, setShowExtraSymptomsField] = useState(false);
  const [showSkipPeriodWarning, setShowSkipPeriodWarning] = useState(false);
  const [showReminderPage, setShowReminderPage] = useState(false);

  const { requestOptIn } = usePushNotificationOptIn();

  const { value: reminderTime, loading: loadingReminderTime } = usePreferences(Keys.TRACKING_REMINDER_TIME);
  const areNotificationsEnabled = reminderTime.hour !== undefined;

  const { value: shouldAskAgainForReminder, set: setShouldAskForReminder } = usePreferences(
    Keys.TRACKING_SURVEY_SHOULD_ASK_FOR_REMINDER,
  );
  const shouldAskForReminder = shouldAskAgainForReminder !== false && !areNotificationsEnabled && Platform.isNative();

  const { isPeriodTrackingEnabled, setShouldAskForPeriod, loadingShouldAskForPeriod } = useShouldAskForPeriod();

  const defaultVisibleQuestions = [
    'generalState',
    'pain',
    'symptoms',
    'vaginalDischarges',
    'sexualActivity',
    'bodyTemperature',
    'notes',
  ];
  const { value: visibleQuestions, set: setVisibleQuestions } = usePreferenceQuery<string[]>(
    Keys.TRACKING_SURVEY_VISIBLE_QUESTIONS,
    defaultVisibleQuestions,
    open,
  );
  const { value: categoriesOpen, set: setCategoriesOpen } = usePreferenceQuery<boolean>(
    Keys.TRACKING_SURVEY_CATEGORIES_OPEN,
    true,
    open,
  );

  const questionOptions = useMemo(
    () => [
      { label: t('symptomTracking.survey.howAreYouToday'), value: 'generalState', icon: QUESTION_ICONS.generalState },
      { label: t('symptomTracking.survey.pain.painQuestion'), value: 'pain', icon: QUESTION_ICONS.pain },
      { label: t('symptomTracking.survey.symptoms.otherSymptoms'), value: 'symptoms', icon: QUESTION_ICONS.symptoms },
      {
        label: t('symptomTracking.survey.vaginalDischarges.title'),
        value: 'vaginalDischarges',
        icon: QUESTION_ICONS.vaginalDischarges,
      },
      {
        label: t('symptomTracking.survey.sexualActivity.title'),
        value: 'sexualActivity',
        icon: QUESTION_ICONS.sexualActivity,
      },
      {
        label: t('symptomTracking.survey.bodyTemperature.title'),
        value: 'bodyTemperature',
        icon: QUESTION_ICONS.bodyTemperature,
      },
      { label: t('symptomTracking.survey.addComment'), value: 'notes', icon: QUESTION_ICONS.notes },
    ],
    [t],
  );

  function toggleQuestion(questionValue: string) {
    const isCurrentlySelected = visibleQuestions.includes(questionValue);
    const newSelection = isCurrentlySelected
      ? visibleQuestions.filter(q => q !== questionValue)
      : [...visibleQuestions, questionValue];
    if (newSelection.length === 0) return;
    setVisibleQuestions(newSelection);
    posthog.capture('tracking.survey.questions_selection_changed', { selectedQuestions: newSelection });
  }

  const { run: loadSurvey, loading: isLoadingSurveyData } = useRequest(
    () => ApiSymptomTrackingManager.getSpecificDailyStatus(date.format('YYYY-MM-DD')),
    {
      manual: true,
      onSuccess: data => {
        setSurvey({
          date: data.date as string,
          general_state: data.general_state ?? null,
          menstruation_amount: data.menstruation_amount ?? 0,
          pain_level: data.pain_level ?? null,
          symptoms: new Set(data.symptoms),
          vaginal_discharges: new Set(data.vaginal_discharges),
          sexual_activity: data.sexual_activity ?? null,
          body_temperature: data.body_temperature ?? null,
        });
        setFreeNotes(data.comment ?? '');
        if (data.extra_symptoms) {
          setExtraSymptomsValue(data.extra_symptoms);
          setShowExtraSymptomsField(true);
        }
      },
    },
  );

  const { loading: postingSurvey, run: postSurvey } = useRequest(
    () => {
      const params = {
        ...survey,
        symptoms: Array.from(survey.symptoms),
        vaginal_discharges: Array.from(survey.vaginal_discharges),
        extra_symptoms: extraSymptomsValue,
        comment: freeNotes,
        menstruation_amount: isPeriodTrackingEnabled ? survey.menstruation_amount : 0,
      };
      if (isEditing) return ApiSymptomTrackingManager.patchSurvey(params);
      return ApiSymptomTrackingManager.postSurvey(params);
    },
    {
      manual: true,
      onSuccess: () => {
        clearCache('tracking.history');
        refresh();
        posthog.capture('tracking.survey.completed');
        if (!isEditing && !hasTrackingBeenUsed) {
          requestOptIn('first_tracking');
        }

        // Show reminder page if should ask for reminder and not editing
        if (shouldAskForReminder && !isEditing) {
          setShowReminderPage(true);
        } else {
          onClose();
        }
      },
      onError: e => {
        console.error(e);
      },
    },
  );

  function chooseGeneralState(value: number) {
    setSurvey(s => ({ ...s, general_state: value }));
    posthog.capture('tracking.survey.general_state_chosen');
  }

  function chooseMenstruationAmount(value: number) {
    setSurvey(s => ({ ...s, menstruation_amount: value }));
    posthog.capture('tracking.survey.menstruation_amount_chosen');
  }

  function toggleDeactivatePeriodTrackingWarning() {
    setShowSkipPeriodWarning(prev => !prev);
    posthog.capture('tracking.survey.no_menstruation_chosen');
  }

  function validateSkipPeriod() {
    setShouldAskForPeriod(false);
    chooseMenstruationAmount(0);
  }

  function handleReminderPageClose() {
    setShowReminderPage(false);
    onClose();
  }

  function handleReminderEnabled() {
    // Handle reminder enabled, then close
    handleReminderPageClose();
  }

  function handleReminderDeferred() {
    posthog.capture('tracking.reminder_setting_deferred');
    handleReminderPageClose();
    setShouldAskForReminder('false');
  }

  function handleModalClose() {
    setShowReminderPage(false);
    setShowExtraSymptomsField(false);
    setShowSkipPeriodWarning(false);
    onClose();
  }

  function choosePainAmount(value: number) {
    setSurvey(s => ({ ...s, pain_level: value }));
    posthog.capture('tracking.survey.pain_level_chosen');
  }

  function isSymptomSelected(symptom: string) {
    return survey.symptoms.has(symptom);
  }

  function toggleSymptom(symptom: string) {
    if (isSymptomSelected(symptom))
      setSurvey(oldSurvey => ({
        ...oldSurvey,
        symptoms: new Set([...oldSurvey.symptoms].filter(item => item !== symptom)),
      }));
    else setSurvey(oldSurvey => ({ ...oldSurvey, symptoms: new Set([...oldSurvey.symptoms, symptom]) }));
  }

  function renderSymptomButton(symptomKey: string, label: string) {
    return (
      <AnswerButton key={label} selected={isSymptomSelected(symptomKey)} onClick={() => toggleSymptom(symptomKey)}>
        <span>{label}</span>
        <div className="size-2 rounded-full" style={{ backgroundColor: colorHash.hex(symptomKey) }} />
      </AnswerButton>
    );
  }

  function isVaginalDischargeSelected(vaginalDischarge: string) {
    return survey.vaginal_discharges.has(vaginalDischarge);
  }

  function toggleVaginalDischarge(vaginalDischarge: string) {
    if (isVaginalDischargeSelected(vaginalDischarge)) {
      setSurvey(oldSurvey => ({
        ...oldSurvey,
        vaginal_discharges: new Set([...oldSurvey.vaginal_discharges].filter(item => item !== vaginalDischarge)),
      }));
      return;
    }
    // "Aucune" is exclusive with the other options: picking it clears the rest, and picking any other
    // option clears "Aucune"
    if (vaginalDischarge === 'NONE') {
      setSurvey(oldSurvey => ({ ...oldSurvey, vaginal_discharges: new Set(['NONE']) }));
    } else {
      setSurvey(oldSurvey => ({
        ...oldSurvey,
        vaginal_discharges: new Set([...oldSurvey.vaginal_discharges].filter(item => item !== 'NONE')).add(
          vaginalDischarge,
        ),
      }));
    }
  }

  function renderVaginalDischargeButton(vaginalDischargeKey: string, label: string) {
    return (
      <AnswerButton
        key={label}
        selected={isVaginalDischargeSelected(vaginalDischargeKey)}
        onClick={() => toggleVaginalDischarge(vaginalDischargeKey)}
      >
        <span>{label}</span>
        {vaginalDischargeKey === 'NONE' ? (
          <CloseOutlined aria-hidden style={{ fontSize: 12, color: VAGINAL_DISCHARGE_COLORS.NONE }} />
        ) : (
          <div
            className="size-2 rounded-full"
            style={{ backgroundColor: VAGINAL_DISCHARGE_COLORS[vaginalDischargeKey] }}
          />
        )}
      </AnswerButton>
    );
  }

  function chooseSexualActivity(value: DailyStatus['sexual_activity']) {
    setSurvey(s => ({ ...s, sexual_activity: value }));
    posthog.capture('tracking.survey.sexual_activity_chosen');
  }

  function renderSexualActivityButton(value: NonNullable<DailyStatus['sexual_activity']>, label: string) {
    const icon =
      value === 'UNPROTECTED' ? (
        <CaretDownOutlined style={{ color: SEXUAL_ACTIVITY_COLORS.UNPROTECTED }} />
      ) : value === 'PROTECTED' ? (
        <CaretUpOutlined style={{ color: SEXUAL_ACTIVITY_COLORS.PROTECTED }} />
      ) : (
        <CloseOutlined style={{ color: SEXUAL_ACTIVITY_COLORS.NONE, fontSize: 12, marginTop: 4 }} />
      );
    return (
      <AnswerButton key={value} onClick={() => chooseSexualActivity(value)} selected={survey.sexual_activity === value}>
        <span>{label}</span>
        <div className="text-center">{icon}</div>
      </AnswerButton>
    );
  }

  function renderGeneralStateButton(value: number, label: string) {
    const selected = survey.general_state;
    const tone = SEVERITY_TEXT[label];
    return (
      <AnswerButton key={label} onClick={() => chooseGeneralState(value)} selected={selected === value}>
        {value > 2 && <SmileFilled key={`${label}-good`} className={tone} />}
        {value === 2 && <MehFilled key={`${label}-average`} className={tone} />}
        {value < 2 && <FrownFilled key={`${label}-bad`} className={tone} />}
        <span>{t(`symptomTracking.survey.generalState.${label}`)}</span>
      </AnswerButton>
    );
  }

  function renderPainLevelButton(value: number, label: string) {
    const selected = survey.pain_level;
    const signal = 'mb-1 size-5 shrink-0';
    return (
      <AnswerButton key={label} onClick={() => choosePainAmount(value)} selected={selected === value}>
        {value === 0 && (
          <Signal0 key={`${label}-thunderbolt`} stroke="currentColor" className={cn(signal, SEVERITY_TEXT.veryGood)} />
        )}
        {value === 1 && (
          <Signal1 key={`${label}-thunderbolt`} stroke="currentColor" className={cn(signal, SEVERITY_TEXT.good)} />
        )}
        {value === 2 && (
          <Signal2 key={`${label}-thunderbolt`} stroke="currentColor" className={cn(signal, SEVERITY_TEXT.average)} />
        )}
        {value === 3 && (
          <Signal3 key={`${label}-thunderbolt`} stroke="currentColor" className={cn(signal, SEVERITY_TEXT.bad)} />
        )}
        {value === 4 && <ThunderboltFilled key={`${label}-thunderbolt`} className={SEVERITY_TEXT.veryBad} />}
        <span>{t(`symptomTracking.survey.pain.${label}`)}</span>
      </AnswerButton>
    );
  }

  function renderMenstruationAmountButton(value: number, label: string) {
    const selected = survey.menstruation_amount;
    return (
      <AnswerButton key={label} onClick={() => chooseMenstruationAmount(value)} selected={selected === value}>
        <div className="mb-1 flex gap-0.5">
          {Array.from({ length: value }).map((_, index) => (
            <Drop
              fill="currentColor"
              key={`red-drop-${label}-${index}`}
              className="size-3 shrink-0 text-chart-severity-very-bad"
            />
          ))}
          {Array.from({ length: 4 - value }).map((_, index) => (
            <Drop
              fill="currentColor"
              key={`gray-drop-${label}-${index}`}
              className="size-3 shrink-0 text-subtle-foreground"
            />
          ))}
        </div>
        <span>{t(`symptomTracking.survey.menstruation.${label}`)}</span>
      </AnswerButton>
    );
  }

  useEffect(() => {
    if (isEditing && open) loadSurvey();
  }, [isEditing, open, loadSurvey]);

  // The survey scrolls inside the dialog under a sticky save button, so the dialog's own padding and gap move to the scroll area.
  const contentClassName = 'flex max-h-[80dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-content';
  const scrollAreaClassName = cn('max-h-[70vh] overflow-y-auto px-6 pt-6 pb-2', SCROLLBAR_HIDDEN);

  if (loadingShouldAskForPeriod || loadingReminderTime) {
    return (
      <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-content">
          <DialogTitle>{t('symptomTracking.survey.loading')}</DialogTitle>
          <div className="flex items-center justify-center p-10">
            <Spinner className="size-8" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const fertilityStatus = isPeriodTrackingEnabled ? getFertilityStatusForDay(date) : null;
  const fertilityDotClass = fertilityStatus
    ? cn(
        'size-2 shrink-0 rounded-full',
        fertilityStatus.type === 'ovulation'
          ? fertilityStatus.hollow
            ? 'border-2 border-inverse bg-transparent'
            : 'bg-inverse'
          : fertilityStatus.hollow
            ? 'border-2 border-info bg-transparent'
            : 'bg-info',
      )
    : '';
  const fertilityLabel = fertilityStatus
    ? fertilityStatus.type === 'ovulation'
      ? fertilityStatus.hollow
        ? t('symptomTracking.survey.fertility.possibleOvulationDay')
        : t('symptomTracking.survey.fertility.ovulationDay')
      : fertilityStatus.hollow
        ? t('symptomTracking.survey.fertility.possibleFertilityDay')
        : t('symptomTracking.survey.fertility.fertilityDay')
    : '';

  // The date heading of the survey page doubles as the dialog's accessible title.
  const hasVisibleTitle = !(isEditing && isLoadingSurveyData) && !showReminderPage && isPeriodTrackingEnabled;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && handleModalClose()}>
      <DialogContent className={contentClassName}>
        {!hasVisibleTitle && <DialogTitle className="sr-only">{date.format('LL')}</DialogTitle>}
        {isEditing && isLoadingSurveyData ? (
          <div className="flex items-center justify-center p-10">
            <Spinner className="size-8" />
          </div>
        ) : showReminderPage ? (
          /* Reminder Page */
          <div className={scrollAreaClassName}>
            <div className="mb-8 rounded-md p-5">
              <SectionTitle>{t('symptomTracking.survey.reminder.question')}</SectionTitle>
              <p className="mt-6 mb-1">{t('symptomTracking.survey.reminder.explain')}</p>
              <div className="mt-4 flex flex-col gap-3">
                <SymptomTrackingReminder
                  mode="callToAction"
                  onReminderEnabled={handleReminderEnabled}
                  hidePeriodReminder={true}
                />
                <Button key="reminder-no-thanks" variant="secondary" size="lg" onClick={handleReminderDeferred}>
                  {t('symptomTracking.survey.reminder.noThanks')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Survey Page */
          <>
            <div className={scrollAreaClassName}>
              {/* Period Section - Moved before General State */}
              {isPeriodTrackingEnabled && (
                <>
                  <div className="text-left">
                    <DialogTitle asChild>
                      <h3>{date.format('LL')}</h3>
                    </DialogTitle>
                    {fertilityStatus && (
                      <>
                        <div className="mb-2 flex items-center gap-2">
                          <span className={fertilityDotClass} />
                          <span>{fertilityLabel}</span>
                        </div>
                        {fertilityStatus.hollow && (
                          <Alert variant="warning">
                            <WarningFilled className="translate-y-0.5" />
                            <AlertDescription>
                              {t('symptomTracking.survey.fertility.predictionWarning')}
                            </AlertDescription>
                          </Alert>
                        )}
                      </>
                    )}
                    <SectionTitle>
                      {QUESTION_ICONS.period} {t('symptomTracking.survey.menstruation.periodQuestion')}
                    </SectionTitle>
                  </div>
                  <AnswerRow>
                    {renderMenstruationAmountButton(0, 'none')}
                    {renderMenstruationAmountButton(1, 'low')}
                    {renderMenstruationAmountButton(2, 'medium')}
                    {renderMenstruationAmountButton(3, 'high')}
                    {renderMenstruationAmountButton(4, 'veryHigh')}
                  </AnswerRow>
                  <div className="mb-4">
                    <Button
                      variant="link-warning"
                      key="period-no-period"
                      onClick={toggleDeactivatePeriodTrackingWarning}
                    >
                      <CloseOutlined />
                      <span>{t('symptomTracking.survey.menstruation.noPeriod')}</span>
                    </Button>
                    {showSkipPeriodWarning && (
                      <p className="text-left text-foreground">
                        <WarningFilled className="mr-1 text-warning" />
                        <Trans
                          i18nKey="symptomTracking.survey.menstruation.skipPeriodWarning"
                          components={{ b: <b /> }}
                        />
                        <br />
                        <Button
                          variant="destructive-subtle"
                          key="period-no-period-understood"
                          onClick={() => validateSkipPeriod()}
                        >
                          <CheckOutlined />
                          <span>{t('symptomTracking.survey.menstruation.understood')}</span>
                        </Button>
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Question selector */}
              <Accordion
                type="single"
                collapsible
                value={categoriesOpen ? 'categories' : ''}
                onValueChange={value => setCategoriesOpen(value === 'categories')}
              >
                <AccordionItem value="categories" variant="bordered">
                  <AccordionTrigger className="items-center hover:no-underline">
                    <span className="flex items-center gap-2">
                      <EyeOutlined /> Catégories suivies
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-2">
                    <p className="text-xs text-subtle-foreground">
                      Personnalisez votre suivi en activant ou désactivant des questions.
                    </p>
                    <div className="relative after:pointer-events-none after:absolute after:top-0 after:right-0 after:bottom-0 after:z-[1] after:w-14 after:bg-gradient-to-l after:from-muted after:to-transparent">
                      <div className={cn('flex gap-2 overflow-x-auto py-1 pr-14 pl-1', SCROLLBAR_HIDDEN)}>
                        {questionOptions.map(option => (
                          <Button
                            key={option.value}
                            variant="outline"
                            selected={visibleQuestions.includes(option.value)}
                            className="shrink-0 whitespace-nowrap data-[selected=false]:text-subtle-foreground data-[selected=false]:[&_.anticon]:text-subtle-foreground data-[selected=false]:[&_svg]:fill-subtle-foreground"
                            onClick={e => {
                              e.currentTarget.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'center',
                              });
                              toggleQuestion(option.value);
                            }}
                          >
                            {option.icon} {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* General State Section */}
              {visibleQuestions.includes('generalState') && (
                <>
                  <SectionTitle>
                    {QUESTION_ICONS.generalState} {t('symptomTracking.survey.howAreYouToday')}
                  </SectionTitle>
                  <AnswerRow>
                    {renderGeneralStateButton(4, 'veryGood')}
                    {renderGeneralStateButton(3, 'good')}
                    {renderGeneralStateButton(2, 'average')}
                    {renderGeneralStateButton(1, 'bad')}
                    {renderGeneralStateButton(0, 'veryBad')}
                  </AnswerRow>
                </>
              )}

              {/* Pain Section */}
              {visibleQuestions.includes('pain') && (
                <>
                  <SectionTitle>
                    {QUESTION_ICONS.pain} {t('symptomTracking.survey.pain.painQuestion')}
                  </SectionTitle>
                  <AnswerRow>
                    {renderPainLevelButton(0, 'none')}
                    {renderPainLevelButton(1, 'light')}
                    {renderPainLevelButton(2, 'moderate')}
                    {renderPainLevelButton(3, 'strong')}
                    {renderPainLevelButton(4, 'intense')}
                  </AnswerRow>
                </>
              )}

              {/* Symptoms Section */}
              {visibleQuestions.includes('symptoms') && (
                <>
                  <SectionTitle>
                    {QUESTION_ICONS.symptoms} {t('symptomTracking.survey.symptoms.otherSymptoms')}
                  </SectionTitle>
                  <AnswerRow twoRows>
                    {renderSymptomButton('FATIGUE', t('symptomTracking.survey.symptoms.FATIGUE'))}
                    {renderSymptomButton('DIGESTIVE', t('symptomTracking.survey.symptoms.DIGESTIVE'))}
                    {renderSymptomButton('BELLY_CRAMP', t('symptomTracking.survey.symptoms.BELLY_CRAMP'))}
                    {renderSymptomButton('MOOD', t('symptomTracking.survey.symptoms.MOOD'))}
                    {renderSymptomButton('BACK_PAIN', t('symptomTracking.survey.symptoms.BACK_PAIN'))}
                    {renderSymptomButton('HEADACHE', t('symptomTracking.survey.symptoms.HEADACHE'))}
                    {renderSymptomButton('NAUSEA', t('symptomTracking.survey.symptoms.NAUSEA'))}
                    {renderSymptomButton('BLOATING', t('symptomTracking.survey.symptoms.BLOATING'))}
                    {renderSymptomButton('DIZZINESS', t('symptomTracking.survey.symptoms.DIZZINESS'))}
                    {renderSymptomButton('URINARY', t('symptomTracking.survey.symptoms.URINARY'))}
                    {renderSymptomButton('BLEEDING', t('symptomTracking.survey.symptoms.BLEEDING'))}
                    {renderSymptomButton('SEXUAL', t('symptomTracking.survey.symptoms.SEXUAL'))}
                    {renderSymptomButton('MENTAL', t('symptomTracking.survey.symptoms.MENTAL'))}
                  </AnswerRow>
                  {showExtraSymptomsField && (
                    <div className="mb-4 w-full">
                      <Textarea
                        rows={5}
                        autoCorrect="on"
                        value={extraSymptomsValue}
                        onChange={e => setExtraSymptomsValue(e.target.value)}
                        placeholder={t('symptomTracking.survey.symptoms.otherSymptomsDescription')}
                      />
                    </div>
                  )}
                  {!showExtraSymptomsField && (
                    <Button
                      key="other-symptoms"
                      variant="secondary"
                      onClick={() => {
                        setShowExtraSymptomsField(true);
                        posthog.capture('tracking.survey.symptoms_chosen');
                      }}
                    >
                      <PlusOutlined />
                      {t('symptomTracking.survey.symptoms.other')}
                    </Button>
                  )}
                </>
              )}

              {/* Vaginal Discharges Section */}
              {visibleQuestions.includes('vaginalDischarges') && (
                <>
                  <SectionTitle>
                    {QUESTION_ICONS.vaginalDischarges} {t('symptomTracking.survey.vaginalDischarges.title')}
                  </SectionTitle>
                  <AnswerRow>
                    {renderVaginalDischargeButton('NONE', t('symptomTracking.survey.vaginalDischarges.NONE'))}
                    {renderVaginalDischargeButton('SPOTTING', t('symptomTracking.survey.vaginalDischarges.SPOTTING'))}
                    {renderVaginalDischargeButton('STICKY', t('symptomTracking.survey.vaginalDischarges.STICKY'))}
                    {renderVaginalDischargeButton('CREAMY', t('symptomTracking.survey.vaginalDischarges.CREAMY'))}
                    {renderVaginalDischargeButton('EGG_WHITE', t('symptomTracking.survey.vaginalDischarges.EGG_WHITE'))}
                    {renderVaginalDischargeButton('UNUSUAL', t('symptomTracking.survey.vaginalDischarges.UNUSUAL'))}
                  </AnswerRow>
                </>
              )}

              {/* Sexual Activity Section */}
              {visibleQuestions.includes('sexualActivity') && (
                <>
                  <SectionTitle>
                    {QUESTION_ICONS.sexualActivity} {t('symptomTracking.survey.sexualActivity.title')}
                  </SectionTitle>
                  <AnswerRow>
                    {renderSexualActivityButton('NONE', t('symptomTracking.survey.sexualActivity.NONE'))}
                    {renderSexualActivityButton('PROTECTED', t('symptomTracking.survey.sexualActivity.PROTECTED'))}
                    {renderSexualActivityButton('UNPROTECTED', t('symptomTracking.survey.sexualActivity.UNPROTECTED'))}
                  </AnswerRow>
                </>
              )}

              {/* Body Temperature Section */}
              {visibleQuestions.includes('bodyTemperature') && (
                <>
                  <SectionTitle>
                    {QUESTION_ICONS.bodyTemperature} {t('symptomTracking.survey.bodyTemperature.title')}
                  </SectionTitle>
                  <div className="relative w-32">
                    <Input
                      type="number"
                      inputMode="decimal"
                      aria-label={t('symptomTracking.survey.bodyTemperature.title')}
                      value={survey.body_temperature ?? ''}
                      onChange={e =>
                        setSurvey(s => ({
                          ...s,
                          body_temperature: e.target.value === '' ? null : Number(e.target.value),
                        }))
                      }
                      className="pr-9"
                      step={0.1}
                      min={30}
                      max={45}
                    />
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground"
                    >
                      °C
                    </span>
                  </div>
                </>
              )}

              {/* Notes Section */}
              {visibleQuestions.includes('notes') && (
                <>
                  <SectionTitle>
                    {QUESTION_ICONS.notes} {t('symptomTracking.survey.addComment')}
                  </SectionTitle>
                  <div className="mb-4 w-full">
                    <Textarea
                      rows={5}
                      value={freeNotes}
                      onChange={e => setFreeNotes(e.target.value)}
                      placeholder={t('symptomTracking.survey.freeTextDescription')}
                    />
                  </div>
                </>
              )}

              {!isPeriodTrackingEnabled && (
                <div className="text-left">
                  <SectionTitle>
                    {QUESTION_ICONS.period} {t('symptomTracking.survey.menstruation.periodQuestion')}
                  </SectionTitle>

                  <div className="flex items-center justify-between gap-3">
                    <p className="m-0">{t('symptomTracking.tracking.disabled')}</p>
                    <Button
                      variant="link"
                      onClick={() => {
                        setShouldAskForPeriod(true);
                        posthog.capture('tracking.period_tracking_reactivated');
                      }}
                    >
                      {t('symptomTracking.tracking.reactivate')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Fixed Save Button */}
            <div className="mt-auto border-t border-border bg-card px-6 py-4">
              <Button size="xl" className="w-full" loading={postingSurvey} onClick={postSurvey}>
                {t('symptomTracking.survey.saveFollowUp')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
